import {
  PaymentMethod,
  PaymentSystem,
} from '~/shared/constants/paymentConstants'
import { getAxonItem } from '~/shared/helpers/getAxonItems'
import { getCurrentEnv } from '~/shared/helpers/getCurrentEnv'
import { ScreenName, eventLogger } from '~/shared/services/eventLogger'
import { googleAnalyticsLogger } from '~/shared/services/googleAnalytics'
import { pinterestPixelService } from '~/shared/services/pinterestPixelService'
import { tiktokPixelService } from '~/shared/services/tiktokPixelService'
import { TProductId } from '~/shared/types/subscriptionsTypes'

const AIRTABLE_PROXY_URL = '/hooks/v1/events/proxy/airtable'
const UPSELL_PRICES = [9.49, 10.49, 11.49, 14.49, 38, 154]

const getAirtablePaymentSystem = ({
  isPayPal,
  paymentSystem,
}: {
  isPayPal: boolean
  paymentSystem: PaymentSystem
}): string => {
  if (isPayPal) return 'paypal'
  if (paymentSystem === PaymentSystem.STRIPE) return 'stripe_direct'
  return 'stripe'
}

const sendAirtableWebhook = ({
  email,
  productId,
  price,
  trialPrice,
  currency,
  subscriptionId,
  paymentMethod,
  paymentSystem,
  countryCode,
  utmTags,
}: {
  email: string
  productId: TProductId
  price: number
  trialPrice?: number
  currency?: string
  subscriptionId: string
  paymentMethod?: PaymentMethod
  paymentSystem: PaymentSystem
  countryCode?: string
  utmTags?: Record<string, string>
}): void => {
  const amountReal = Math.round((trialPrice || price) * 100) / 100

  if (getCurrentEnv() !== 'prod') return
  if (!productId) return
  if (UPSELL_PRICES.includes(amountReal)) return

  const isPayPal = paymentMethod === PaymentMethod.PAYPAL
  fetch(AIRTABLE_PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      data: {
        user_email: email,
        product_id: productId,
        amount_real: amountReal,
        currency,
        transaction_id: subscriptionId,
        payment_system: getAirtablePaymentSystem({ isPayPal, paymentSystem }),
        event_type: isPayPal
          ? 'BILLING.SUBSCRIPTION.ACTIVATED'
          : 'charge.succeeded',
        charges_count: 1,
        status: 'active',
        country: countryCode,
        ...utmTags,
      },
    }),
  }).catch(() => undefined)
}

export const logSuccessfulPayment = ({
  email,
  productId,
  productKey,
  price,
  predictedLtv,
  currency,
  periodName,
  periodQuantity,
  trialPrice,
  trialPeriodDays,
  subscriptionId,
  discountApplied,
  uuid,
  paymentMethod,
  screenName,
  paymentSystem,
  stripeAccountId,
  stripeAccountName,
  isForce3DS,
  countryCode,
  utmTags,
  paymentProcessor,
}: {
  email: string
  productId: TProductId
  productKey?: string
  price: number
  predictedLtv?: number
  currency?: string
  periodName: string
  periodQuantity: number
  trialPrice?: number
  trialPeriodDays?: number
  subscriptionId: string
  discountApplied?: string
  uuid: string
  paymentMethod?: PaymentMethod
  screenName: ScreenName
  paymentSystem: PaymentSystem
  stripeAccountId?: string
  stripeAccountName?: string
  isForce3DS: boolean
  countryCode?: string
  utmTags?: Record<string, string>
  paymentProcessor?: string
}): void => {
  if (screenName !== ScreenName.UPSELL_OFFER) {
    sendAirtableWebhook({
      email,
      productId,
      price,
      trialPrice,
      currency,
      subscriptionId,
      paymentMethod,
      paymentSystem,
      countryCode,
      utmTags,
    })
  }
  if (trialPeriodDays && screenName !== ScreenName.UPSELL_OFFER) {
    window.fbq(
      'track',
      'StartTrial',
      {
        value: trialPrice,
        currency,
        subscription_id: subscriptionId,
        // This field and similar ones below have been added to avoid FB pixel issue. https://github.com/facebook/facebook-nodejs-business-sdk/issues/164
        subscription_sid: subscriptionId,
      },
      { eventID: uuid },
    )
  }

  if (screenName !== ScreenName.UPSELL_OFFER) {
    window.fbq(
      'track',
      'Subscribe',
      {
        value: trialPrice || price,
        currency,
        subscription_id: subscriptionId,
        subscription_sid: subscriptionId,
      },
      { eventID: uuid },
    )

    window.fbq(
      'track',
      'Purchase',
      {
        value: predictedLtv || trialPrice || price,
        currency,
        subscription_id: subscriptionId,
        subscription_sid: subscriptionId,
      },
      { eventID: uuid },
    )

    googleAnalyticsLogger.logPurchaseCompleted({
      subscriptionId,
      price: trialPrice || price,
      productId,
      currency,
    })
  }

  if (currency) {
    tiktokPixelService.trackPurchase({
      value: trialPrice || price,
      currency,
      contentId: productId,
      contentName: productKey,
      eventId: uuid,
    })
  }

  eventLogger.logPurchaseCompleted({
    email,
    productId,
    productKey,
    priceDetails: {
      price,
      trial: !!trialPrice,
      currency,
    },
    paymentMethod,
    transactionId: subscriptionId,
    screenName,
    paymentSystem,
    stripeAccountId,
    stripeAccountName,
    discountApplied,
    isForce3DS,
    paymentProcessor,
  })

  if (screenName !== ScreenName.UPSELL_OFFER) {
    window?.axon?.('track', 'purchase', {
      value: trialPrice || price,
      currency: currency?.toUpperCase(),
      user_id: uuid,
      transaction_id: subscriptionId,
      items: [
        getAxonItem({
          id: subscriptionId,
          periodQuantity,
          periodName,
          trialPrice,
          price,
        }),
      ],
    })
  }

  window?.hj?.('event', 'purchase')

  pinterestPixelService.trackCheckout({
    value: trialPrice || price,
    currency: currency || '',
    orderId: uuid,
    productId: subscriptionId,
    productName: `${periodQuantity}_${periodName}_${trialPrice || price}`,
    productPrice: trialPrice || price,
  })
}
